import { useState } from 'react';
import { useI18n } from '@/i18n';
import {
  Badge,
  Button,
  Card,
  Input,
  Spinner,
} from '@/components/ui';
import {
  useCACCreateLicenseTemplate,
  useCACDeprecateLicenseTemplate,
  useCACLicenseTemplate,
  useCACLicenseTemplates,
  useCACUpdateLicenseTemplate,
} from '@/features/cac';
import type { LicenseCommercialMode, LicenseTemplate, LicenseTemplateStatus } from '@/features/cac/types';

const MODE_OPTIONS: { value: LicenseCommercialMode; label: string }[] = [
  { value: 'none', label: 'None (Internal only)' },
  { value: 'free', label: 'Free' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'private', label: 'Private' },
];

const STATUS_BADGE_CLASS: Record<LicenseTemplateStatus, string> = {
  active: 'bg-green-50 text-green-700 border-green-200',
  deprecated: 'bg-gray-50 text-gray-500 border-gray-200',
};

const MODE_BADGE_CLASS: Record<LicenseCommercialMode, string> = {
  none: 'bg-slate-50 text-slate-600 border-slate-200',
  free: 'bg-blue-50 text-blue-700 border-blue-200',
  commercial: 'bg-amber-50 text-amber-700 border-amber-200',
  private: 'bg-purple-50 text-purple-700 border-purple-200',
};

interface FormData {
  slug: string;
  display_name: string;
  commercial_mode: LicenseCommercialMode;
  allows_public_pricing: boolean;
  allows_private_offers: boolean;
  requires_partner_approval: boolean;
  terms_summary: string;
  terms_text: string;
}

function emptyForm(): FormData {
  return {
    slug: '',
    display_name: '',
    commercial_mode: 'free',
    allows_public_pricing: false,
    allows_private_offers: false,
    requires_partner_approval: false,
    terms_summary: '',
    terms_text: '',
  };
}

function publicErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Operation failed.';
  const maybeAxios = error as {
    response?: { data?: { detail?: { message?: string } } };
  };
  return maybeAxios.response?.data?.detail?.message ?? 'Operation failed. Please retry.';
}

export function LicenseTemplatesPage() {
  const { t: _t } = useI18n();
  void _t;

  const [filterStatus, setFilterStatus] = useState<LicenseTemplateStatus | ''>('');
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showVersions, setShowVersions] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm());

  const templatesQuery = useCACLicenseTemplates(filterStatus || undefined);
  const versionQuery = useCACLicenseTemplate(showVersions ?? '');
  const createMutation = useCACCreateLicenseTemplate();
  const updateMutation = useCACUpdateLicenseTemplate();
  const deprecateMutation = useCACDeprecateLicenseTemplate();

  const templates = templatesQuery.data ?? [];

  const resetForm = () => {
    setForm(emptyForm());
    setShowCreate(false);
    setEditingSlug(null);
  };

  const handleSave = async () => {
    if (editingSlug) {
      await updateMutation.mutateAsync({ slug: editingSlug, data: form });
    } else {
      await createMutation.mutateAsync(form);
    }
    resetForm();
  };

  const handleEdit = (t: LicenseTemplate) => {
    setForm({
      slug: t.slug,
      display_name: t.display_name,
      commercial_mode: t.commercial_mode,
      allows_public_pricing: t.allows_public_pricing,
      allows_private_offers: t.allows_private_offers,
      requires_partner_approval: t.requires_partner_approval,
      terms_summary: '',
      terms_text: '',
    });
    setEditingSlug(t.slug);
    setShowCreate(false);
  };

  const handleDeprecate = async (slug: string) => {
    if (window.confirm('Deprecate this template? Existing publications will remain valid but new publications cannot select it.')) {
      await deprecateMutation.mutateAsync(slug);
    }
  };

  return (
    <div className="space-y-6" data-testid="license-templates-page">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">License Templates</h2>
        <Button
          variant="default"
          size="sm"
          onClick={() => {
            resetForm();
            setShowCreate(true);
          }}
        >
          + New Template
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <select
          className="border border-gray-200 dark:border-gray-700 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-900"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as LicenseTemplateStatus | '')}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="deprecated">Deprecated</option>
        </select>
      </div>

      {(createMutation.isError || updateMutation.isError || deprecateMutation.isError) ? (
        <Card className="p-4 border-destructive/30 text-destructive">
          {publicErrorMessage(createMutation.error ?? updateMutation.error ?? deprecateMutation.error)}
        </Card>
      ) : null}

      {(showCreate || editingSlug) ? (
        <Card className="p-4 space-y-3">
          <h3 className="font-medium">{editingSlug ? `Edit: ${editingSlug}` : 'New License Template'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              placeholder="Slug (e.g. my-custom-license)"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              disabled={!!editingSlug}
            />
            <Input
              placeholder="Display name"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            />
            <select
              className="border border-gray-200 dark:border-gray-700 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-900"
              value={form.commercial_mode}
              onChange={(e) => setForm({ ...form, commercial_mode: e.target.value as LicenseCommercialMode })}
            >
              {MODE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <Input
              placeholder="Terms summary"
              value={form.terms_summary}
              onChange={(e) => setForm({ ...form, terms_summary: e.target.value })}
            />
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.allows_public_pricing}
                onChange={(e) => setForm({ ...form, allows_public_pricing: e.target.checked })}
              />
              Public pricing
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.allows_private_offers}
                onChange={(e) => setForm({ ...form, allows_private_offers: e.target.checked })}
              />
              Private offers
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.requires_partner_approval}
                onChange={(e) => setForm({ ...form, requires_partner_approval: e.target.checked })}
              />
              Requires partner approval
            </label>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Terms text (markdown)</label>
            <textarea
              className="w-full border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-900 min-h-[200px]"
              value={form.terms_text}
              onChange={(e) => setForm({ ...form, terms_text: e.target.value })}
              placeholder="Full legal terms in markdown format..."
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={resetForm}>Cancel</Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </Card>
      ) : null}

      {templatesQuery.isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : templates.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">No license templates found.</Card>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => (
            <Card key={t.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{t.display_name}</h3>
                    <Badge variant="outline" className={STATUS_BADGE_CLASS[t.status] ?? ''}>{t.status}</Badge>
                    <Badge variant="outline" className={MODE_BADGE_CLASS[t.commercial_mode] ?? ''}>{t.commercial_mode}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">slug: {t.slug} · v{t.current_version}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowVersions(t.slug)}>
                    Versions
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(t)}>
                    Edit
                  </Button>
                  {t.status === 'active' ? (
                    <Button variant="outline" size="sm" onClick={() => handleDeprecate(t.slug)}>
                      Deprecate
                    </Button>
                  ) : null}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showVersions ? (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">Version History: {showVersions}</h3>
            <Button variant="outline" size="sm" onClick={() => setShowVersions(null)}>Close</Button>
          </div>
          {versionQuery.isLoading ? (
            <Spinner />
          ) : versionQuery.data ? (
            <div className="space-y-3">
              {versionQuery.data.versions.map((v) => (
                <Card key={v.id} className="p-3 border-l-2 border-l-blue-300">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs font-mono">v{v.version}</Badge>
                    <span className="text-xs text-muted-foreground">{new Date(v.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{v.terms_summary}</p>
                  {v.terms_text ? (
                    <details className="mt-2">
                      <summary className="text-xs text-blue-600 cursor-pointer">View full terms</summary>
                      <pre className="mt-2 text-xs whitespace-pre-wrap bg-gray-50 dark:bg-gray-900 p-3 rounded-md max-h-[300px] overflow-y-auto">
                        {v.terms_text}
                      </pre>
                    </details>
                  ) : null}
                </Card>
              ))}
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
